/** Values the /partners sign-up form collects. */
export interface PartnerFormValues {
  fullName: string;
  email: string;
  propertyName: string;
  propertyWebsite: string;
  /** Optional answer to "How did you hear about us?". */
  referralSource: string;
}

/** Per-field error messages. A missing key means the field is valid. */
export type PartnerFormErrors = Partial<Record<keyof PartnerFormValues, string>>;

/** Empty form state, used on first render and after a successful submit. */
export const EMPTY_PARTNER_FORM: PartnerFormValues = {
  fullName: "",
  email: "",
  propertyName: "",
  propertyWebsite: "",
  referralSource: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Accepts "hotel.com", "www.hotel.com" and "https://hotel.com/path". Venues rarely type the protocol.
const WEBSITE_PATTERN = /^(https?:\/\/)?[^\s/.]+(\.[^\s/.]+)+(\/\S*)?$/i;

/**
 * Validates the partner sign-up form.
 *
 * @param values - The current form values.
 * @returns An error message for each invalid field. An empty object means the form is valid.
 */
export function validatePartnerForm(values: PartnerFormValues): PartnerFormErrors {
  const errors: PartnerFormErrors = {};

  if (!values.fullName.trim()) errors.fullName = "Your name is required.";

  if (!values.email.trim()) errors.email = "Your email is required.";
  else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email.";

  if (!values.propertyName.trim()) errors.propertyName = "Property name is required.";

  if (!values.propertyWebsite.trim()) errors.propertyWebsite = "Property website is required.";
  else if (!WEBSITE_PATTERN.test(values.propertyWebsite.trim()))
    errors.propertyWebsite = "Please enter a valid website, like hotel.com.";

  return errors;
}

/**
 * Trims every field so stray spaces do not reach the database or Slack.
 *
 * @param values - The raw form values.
 * @returns The same values with surrounding whitespace removed.
 */
export function trimPartnerForm(values: PartnerFormValues): PartnerFormValues {
  return {
    fullName: values.fullName.trim(),
    email: values.email.trim(),
    propertyName: values.propertyName.trim(),
    propertyWebsite: values.propertyWebsite.trim(),
    referralSource: values.referralSource.trim(),
  };
}
